// packages/ui/src/components/Button/Button.tsx

import { forwardRef } from 'react';
import type { ReactNode, ButtonHTMLAttributes, Ref } from 'react';

import { Slot } from '@radix-ui/react-slot';

import { cn } from '../../lib/cn';
import { Spinner } from '../Spinner/Spinner';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'success' | 'warning';
  size?: 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  /** Alias de `isLoading` — aceito para compatibilidade; não vaza para o DOM. */
  loading?: boolean;
  disabled?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  fullWidth?: boolean;
  type?: 'button' | 'submit' | 'reset';
  // Renderiza como o filho (Radix Slot pattern) preservando classes/handlers — usado p/ envolver <a>/<Link>.
  asChild?: boolean;
  children: ReactNode;
}

const baseClasses =
  'inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

const variantClasses: Record<NonNullable<ButtonProps['variant']>, string> = {
  primary:
    'bg-primary-500 text-white hover:bg-primary-600 focus:ring-primary-400 dark:bg-[#b44d10] dark:hover:bg-[#903e15]',
  secondary: 'bg-secondary-800 text-white hover:bg-secondary-900 focus:ring-secondary-500',
  outline: 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:ring-gray-400',
  ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 focus:ring-gray-400',
  danger: 'bg-error-500 text-white hover:bg-error-600 focus:ring-error-400',
  success: 'bg-green-500 text-white hover:bg-green-600 focus:ring-green-400',
  warning: 'bg-amber-500 text-white hover:bg-amber-600 focus:ring-amber-400',
};

const sizeClasses: Record<NonNullable<ButtonProps['size']>, string> = {
  sm: 'px-3 py-1.5 text-sm gap-1 rounded',
  md: 'px-4 py-2 text-sm gap-1.5 rounded-lg',
  lg: 'px-6 py-3 text-base gap-2 rounded-lg',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    isLoading = false,
    loading,
    disabled = false,
    leftIcon,
    rightIcon,
    fullWidth = false,
    type = 'button',
    asChild = false,
    children,
    className,
    onClick,
    ...rest
  },
  ref,
) {
  // `loading` é alias de `isLoading`; ambos resolvem para a mesma semântica.
  // Nenhum dos dois vaza para o DOM (excluídos do spread `...rest`).
  const resolvedIsLoading = isLoading || Boolean(loading);
  const isDisabled = disabled || resolvedIsLoading;
  const mergedClassName = cn(
    baseClasses,
    variantClasses[variant],
    sizeClasses[size],
    fullWidth && 'w-full',
    className,
  );

  if (asChild) {
    // Slot exige single child; delega tag/handlers ao filho (<a>, <Link>, etc.).
    // type/disabled não aplicam a elementos não-button — leftIcon/rightIcon ignorados.
    return (
      <Slot
        ref={ref as Ref<HTMLElement>}
        className={mergedClassName}
        aria-disabled={isDisabled || undefined}
        aria-busy={resolvedIsLoading || undefined}
        onClick={isDisabled ? undefined : onClick}
        {...rest}
      >
        {children}
      </Slot>
    );
  }

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-disabled={isDisabled || undefined}
      aria-busy={resolvedIsLoading || undefined}
      className={mergedClassName}
      onClick={isDisabled ? undefined : onClick}
      {...rest}
    >
      {resolvedIsLoading ? (
        <>
          <Spinner size="sm" color="currentColor" aria-label="Carregando" />
          <span>{children}</span>
        </>
      ) : (
        <>
          {leftIcon && (
            <span className="inline-flex shrink-0" aria-hidden="true">
              {leftIcon}
            </span>
          )}
          <span>{children}</span>
          {rightIcon && (
            <span className="inline-flex shrink-0" aria-hidden="true">
              {rightIcon}
            </span>
          )}
        </>
      )}
    </button>
  );
});
